import type { CustomFieldAnswer } from "@/domain/custom-fields/types";

export interface PartnerReservation {
  id: number;
  offerId: number;
  /** A state the API added after this build carries through as its raw value
   *  and is shown nowhere: the list admits states by name, so an unknown one is
   *  invisible rather than rendered in whatever shape happens to fit. */
  state: string;
  activityName: string;
  customerName: string | null;
  /** When the customer ordered. Null on rows the API sends without it — the
   *  portal never stands in an instant it was not given. */
  createdAt: Date | null;
  startTime: Date | null;
  /** What the customer pays, in haléře. Never a float. */
  amountMinor: number;
  /** Issued at capture only — a reservation that is merely held has none. */
  code: string | null;
  /** Absolute instant by which the business owes an answer. Server-computed;
   *  the portal renders it and never derives it. */
  confirmDeadline: Date | null;
  validatedAt: Date | null;
  /** What the customer answered at booking. Empty when nothing was asked. */
  customAnswers: CustomFieldAnswer[];
  /** Why the reservation ended, on a cancelled one. Null on every row cancelled
   *  before the API began sending it — an ending nobody knows, not one to guess. */
  cancellationReason: string | null;
}

export interface ConfirmedReservation {
  id: number;
  code: string | null;
  confirmedAt: Date | null;
}

/** The states a code lookup can answer with. Anything the API sends that is not
 *  named here — or no state at all — reads as `unknown`, never as a paid one. */
export const reservationLookupStates = [
  "pending_payment",
  "pending_confirmation",
  "upcoming",
  "finished",
  "cancelled",
  "unknown",
] as const;

export type ReservationLookupState = (typeof reservationLookupStates)[number];

/** What a partner sees after typing a reservation code at the door. Only a
 *  reservation of the partner's own business is ever found, so an unknown code
 *  and somebody else's code are the same answer. */
export interface ReservationLookup {
  id: number;
  state: ReservationLookupState;
  activityName: string;
  customerName: string | null;
  startTime: Date | null;
  amountMinor: number;
  /** When attendance was recorded. Attendance is a record, not a gate — a
   *  reservation is complete at payment whether this is set or not. */
  validatedAt: Date | null;
  customAnswers: CustomFieldAnswer[];
}

/** Recorded once. A second attempt answers with the first one's instant. */
export interface RecordedArrival {
  validatedAt: Date | null;
}
